import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const values = [
  { icon: "ri-focus-3-line", title: "内容聚焦", desc: "围绕影视剧集与剧情介绍，提供清晰、集中的信息浏览体验。" },
  { icon: "ri-shield-check-line", title: "信息可靠", desc: "条目资料经统一整理，标题、封面与简介保持一一对应。" },
  { icon: "ri-heart-3-line", title: "兴趣优先", desc: "以观众的观看兴趣为出发点，让查找与回顾都更轻松。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 热情的邻居影视内容",
    description:
      "了解热情的邻居影视内容网站的定位、内容方向与联系方式。我们聚焦影视剧集信息与剧情介绍，帮助影视爱好者快速了解与追踪。",
    keywords: "热情的邻居,关于我们,影视内容,剧情介绍,影视信息",
  });

  return (
    <div className="w-full">
      <section className="px-4 md:px-10 py-12 md:py-20 bg-background-100 border-b border-background-200/60">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/15 text-primary-300 text-xs md:text-sm">
            <i className="ri-fire-line" />
            关于热情的邻居
          </span>
          <h1 className="mt-5 font-heading font-black text-3xl md:text-5xl text-foreground-50 leading-tight">
            让每一部好剧，都被认真介绍
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl mx-auto">
            热情的邻居是一个聚焦影视内容的信息平台，围绕“热情的邻居”相关影视作品及热门剧集，提供剧情介绍、
            资料速览与相关推荐，帮助广大影视爱好者全面了解与追踪感兴趣的作品。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors"
            >
              <i className="ri-compass-3-line" />
              开始浏览
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md border border-background-300/60 text-foreground-200 text-sm whitespace-nowrap transition-colors"
            >
              <i className="ri-search-line" />
              搜索影视
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-10 py-12 md:py-16">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 flex items-center gap-2">
              <span className="w-1 h-6 rounded-full bg-primary-500 inline-block" />
              我们的定位
            </h2>
            <p className="mt-5 text-sm md:text-base text-foreground-400 leading-loose">
              我们不做播放与下载，只专注把“内容讲清楚”。在这里，你可以快速查看一部影视作品的基本资料、
              剧情简介与分集内容，也可以通过分类筛选和关键词搜索，找到自己想看的方向。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-400 leading-loose">
              网站目前展示的全部影视数据均为示例虚构内容，仅用于演示浏览、筛选与查找等体验，不代表任何真实作品。
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="p-4 bg-background-100 border border-background-200/60 rounded-lg">
                <span className="block font-heading font-black text-2xl text-accent-500">28+</span>
                <span className="block text-xs text-foreground-500 mt-1">影视条目</span>
              </div>
              <div className="p-4 bg-background-100 border border-background-200/60 rounded-lg">
                <span className="block font-heading font-black text-2xl text-accent-500">5</span>
                <span className="block text-xs text-foreground-500 mt-1">核心板块</span>
              </div>
              <div className="p-4 bg-background-100 border border-background-200/60 rounded-lg">
                <span className="block font-heading font-black text-2xl text-accent-500">每日</span>
                <span className="block text-xs text-foreground-500 mt-1">内容更新</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {values.map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-5 bg-background-100 border border-background-200/60 rounded-lg">
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-secondary-100/70 text-secondary-900">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <span>
                  <span className="block text-sm md:text-base font-semibold text-foreground-50">{item.title}</span>
                  <span className="block text-xs md:text-sm text-foreground-500 mt-1.5 leading-relaxed">
                    {item.desc}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-10 py-12 md:py-16">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-6 md:p-8 bg-primary-950 border border-primary-900/60 rounded-lg">
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50">联系我们</h2>
            <p className="mt-4 text-sm text-foreground-400 leading-relaxed">
              如果你对内容有任何建议、纠错或合作意向，欢迎通过以下方式与编辑部取得联系。
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <span className="flex items-center gap-3 text-sm text-foreground-300">
                <i className="ri-mail-line text-accent-500" />
                hello@hotneighbor.example
              </span>
              <span className="flex items-center gap-3 text-sm text-foreground-300">
                <i className="ri-time-line text-accent-500" />
                周一至周五 09:00 - 18:00
              </span>
              <span className="flex items-center gap-3 text-sm text-foreground-300">
                <i className="ri-map-pin-line text-accent-500" />
                在线内容平台 · 无实体办公地址
              </span>
            </div>
          </div>

          <div className="p-6 md:p-8 bg-background-100 border border-background-200/60 rounded-lg">
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50">版权信息</h2>
            <p className="mt-4 text-sm text-foreground-400 leading-relaxed">
              本站点展示的影视名称、简介及资料均为示例演示内容，仅用于产品功能体验，
              不构成对任何真实作品的介绍或推荐。如涉及第三方权益，请通过上方邮箱与我们联系。
            </p>
            <p className="mt-4 text-xs text-foreground-600 leading-relaxed">
              © {new Date().getFullYear()} 热情的邻居 · 保留所有权利 · 站内不含真实播放、下载与会员服务
            </p>
            <div className="flex items-center gap-3 mt-6">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-300">
                <i className="ri-wechat-line" />
              </span>
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-300">
                <i className="ri-weibo-line" />
              </span>
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-300">
                <i className="ri-mail-line" />
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}